import { useQuery } from '@tanstack/react-query';
import { foundBlockWords, foundStatusWords, type CoastalRow, type FoundRow } from '@claims/core';
import { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import { Link } from 'react-router';
import { actionToneOf, AFTER_SAVE, AGE_BUCKETS, QUEUE_CAP, ageTone, ageWords, capGroup, caseAgeDays, dolLabel, factKindsFor, lightFacts, notACarrierName, oldestWait, pacificStamp, queueWords,
  TODO_GROUPS, UNOPENED_STATUSES, unopenedAction, unopenedItem, workingSplit, type OpenedToday, type TodoBoard, type TodoGroupView, type TodoItem, type UnopenedPage, type UnopenedRec } from '@claims/core';
import { HeldGoneContext, useHoldWhilePointed, type HoldArea } from './list/hold.js';
import { countOf } from './count.js';
import { toneProps } from './action-tone.js';
import { api } from './api.js';
import { ChatterTeam } from './actions.js';
import { allFixes, caseShort, clientLine, MainCaseLink, caseTitle, DoNotUseChip, evidence, firstFix, keyed, PriorityChips, ReplyLine, sfMatterUrl, SidePill, stateCode, UrgentChip, waited } from './components.js';
import { FoundApprove } from './found.js';
import { ApproveClaim, FactAct, FactLines, FlagForRod, INLINE_KINDS, InlineFact, LorEvidenceLine, PoliceApprove, useMe } from './facts.js';
import { Ic } from './icons.js';
import { EmptyState, LIST_SKELETON_ROWS, STATE_WORDS } from './states.js';
import { ActionCell } from './list/ActionCell.js';
import { GroupRow } from './list/GroupRow.js';
import { pageSpan, PAGER_WORDS, showingWords } from './list/list-page.js';
import type { ListSort } from './list/list-sort.js';
import { Pager, type PageMove } from './list/Pager.js';
import { KEY_WORDS, SF_COLUMN_WORDS, sfHrefs, type KeyEntry } from './list/row-key.js';
import { RowKey } from './list/RowKey.js';
import { SfLinks } from './list/SfLinks.js';
import { SinceCell } from './list/SinceCell.js';
import { PlainHeader, SortHeader } from './list/SortHeader.js';
import { queueKeyEntries, unopenedKeyEntries } from './queue-key.js';
import { Decide } from './vendors.js';
import { QUEUE_SORT, sortQueueItems, type QueueSortKey } from './queue-sort.js';
import { SystemWorkingBar, SystemWorkingTag, systemHandles } from './system-working.js';

// The Queue's markup (the three-view design, phase 1, in the working layout Rod approved 2026-10-13 "2a"; in the medrec console's style B
// list since 2026-10-18, its 10-17 redesign). Every piece of row, group-header and bar markup lives in this file, so the layout can change
// without touching the data rules (core todo.ts / queue.ts decide what is in each group and in what order). The layout: one sticky bar; one
// table under it with a sticky column header, every column sortable; sticky group headings (verb-first task, count, one grey line: the
// longest wait and the how-to); one two-line row per record: [Since, red once overdue] [case] [side + carrier + policy] [What to do] [the
// action, a fixed column] [M I] [the key] [›]; the Why, the evidence and the system's own words in the hover and in the row's panel. No
// Status, Urgent or Next-move columns: being in the Queue is "needs you", and urgent rows are first in their group.

const itemKey = (it: TodoItem) => `${it.matter.id}:${it.row.insuranceId}`;
/** A Queue row as the held list compares it (list/hold.ts): its place (group or fold), the record, and what it says and waits on. */
export const queueRowSig = (place: string, it: TodoItem): string =>
  `${place}|${itemKey(it)}|${it.row.status}|${it.row.who ?? ''}|${it.row.lorAsk ? 1 : 0}|${(it.row.facts ?? []).map((f) => `${f.id}:${f.state}`).join(',')}`;
/** Every row of a Queue answer, as `queueRowSig` writes it. */
export function queueSigs(q: TodoBoard): Set<string> {
  const out = new Set<string>();
  for (const g of q.groups) for (const it of g.items) out.add(queueRowSig(g.key, it));
  for (const [place, items] of [['fresh', q.fresh], ['working', q.working], ['parked', q.parked]] as const) for (const it of items) out.add(queueRowSig(place, it));
  return out;
}

/** What a Queue answer's rows are, in order, for the click guard (list/hold.ts useRowsChangedGuard). */
export const queueSigLine = (q: TodoBoard): string => [...queueSigs(q)].join('\n');
/** What an All unopened page's rows are, in order, for the click guard. */
const unopenedSigLine = (p: UnopenedPage): string => p.records.map((r) => JSON.stringify(r)).join('\n');

/** The one sentence a person acts on: for a client reply, "Replied 3 h ago: “first words”" (Kimi's layout pass T8); else the Need line, else
 *  the row's own words, in the Queue's words (no raw system error ever, T4: queueWords). */
const firstWords = (t: string, n = 60) => (t.length > n ? `${t.slice(0, n).replace(/\s+\S*$/, '')}…` : t);
function replyOf(it: TodoItem) { const r = it.row; return r.replies?.find((x) => x.waiting) ?? r.reply ?? null; }
function needText(it: TodoItem, now: Date): string {
  const r = it.row, rep = it.group === 'reply' ? replyOf(it) : null;
  if (rep) return `Replied ${waited(rep.at, now.getTime())} ago: ${rep.media && !rep.text ? 'a photo' : `“${firstWords(rep.text)}”`}`;
  return queueWords(r.need ?? r.text);
}
/** the hover on the Need: the full Need, what is going on, why a person is needed, what the system tried; plain words only (T4) */
function needHover(it: TodoItem, now: Date): string {
  const r = it.row, rep = it.group === 'reply' ? replyOf(it) : null;
  const note = r.lastCall?.note ? `"${queueWords(r.lastCall.note)}"` : null;
  return [rep ? `The client replied ${pacificStamp(rep.at)}: ${rep.text || '(a photo)'}` : queueWords(r.need ?? r.text), r.need ? queueWords(r.text) : null,
    it.why ? `Why: ${it.why}` : null, r.calls && r.lastCall ? queueWords(evidence(r, true)) : null, note, r.lor, r.nextAt ? `Next try ${pacificStamp(r.nextAt)}` : null]
    .filter((x, i, a) => !!x && a.indexOf(x) === i).join('\n');
}

/** The row's action, in the fixed Action column (list/ActionCell): an input and Save for a claim #, policy # or claims phone; else the one
 *  button that unblocks it. */
function QueueAction({ it, onOpen }: { it: TodoItem; onOpen: () => void }) {
  const r = it.row, matterId = it.matter.id;
  const me = useMe().data;
  // a client reply (T8): read the whole thread first; the expansion holds it and the fixes
  // it opens the row's panel, where the reply is read and its fact typed: grey, it only looks (core action_tone.ts)
  if (it.group === 'reply') return <><button type="button" className="btn pri sm" onClick={onOpen} {...toneProps(actionToneOf('console', 'read_reply'))}><Ic name="msg" cls="sm" />Read &amp; reply</button><MainCaseLink r={r} shownByFix={false} /></>;
  const kinds = factKindsFor(r, { lorSend: me?.lorSend === true, lorOnFile: me?.lorOnFile === true, policeOrder: me?.policeOrder === true });
  if (kinds.length && INLINE_KINDS.has(kinds[0]!)) return <><InlineFact kind={kinds[0]!} r={r} matterId={matterId} /><MainCaseLink r={r} shownByFix={false} /></>;
  // a second client whose work is on the main client's case (core gaps.ts onMainCase, companion rows): that case is the primary action;
  // "No insurance" stays as the secondary one, for a reply a person confirms (Codex 10-14 #46 v10)
  if (r.secondClient && r.fix === 'companion_main' && r.status === 'need') return <>{firstFix(r, matterId)}{me?.writeBack === true && kinds.length ? <FactAct kinds={kinds} r={r} matterId={matterId} compact secondary /> : null}</>;
  if (me?.writeBack === true && kinds.length) return <><FactAct kinds={kinds} r={r} matterId={matterId} compact /><MainCaseLink r={r} shownByFix={false} /></>;
  const fix = r.status === 'need' || r.lorAsk ? firstFix(r, matterId) : null;
  return <>{fix}<MainCaseLink r={r} shownByFix={!!fix && r.fix === 'companion_main'} /></>;
}

/** THE QUEUE'S TABLE (ported 2026-10-18 from the medrec console's style B list, DESIGN_SYSTEM 7: one table, its column header under the bar,
 *  groups as heading rows, two-line 48 px rows). The columns, left to right: Since (the wait; All unopened: the case's age), Case, Record,
 *  What to do, Action (one fixed width, so its edge never moves), Open in Salesforce (M and I), the row's key (the circle i), and the row's own
 *  panel (›: why, the last call, every fix, What happened, Chatter, flag for Rod). Every column but the last two sorts, inside each group
 *  (queue-sort.ts; the address keeps it). Each row is its own `tbody` (`.qrowwrap`): the row, the Approve strip under it, and its panel. */
const COLS = 8;
export interface QueueSortProps { sort: ListSort<QueueSortKey>; onSort: (k: QueueSortKey) => void }
const NO_SORT: QueueSortProps = { sort: QUEUE_SORT.default, onSort: () => undefined };

/** Every data column sorts. On the Queue's tabs the rows in hand sort inside their groups; on All unopened (one page of 100 out of thousands)
 *  the press goes to the server, which sorts the whole filtered list before it pages (the Ins Creation chat, 10-18; core unopened.ts). */
export function QueueTable({ children, sort, onSort, since = 'Since', foot, cardRef, area, held = false }: { children: ReactNode; since?: string;
  /** a paged list's footer (All unopened: the count, the rows a page holds and the shared pager), under the table inside the card */ foot?: ReactNode;
  cardRef?: Ref<HTMLDivElement>;
  /** the table's pointer handlers and click guard, and whether a newer answer waits for the pointer to leave (list/hold.ts) */
  area?: HoldArea; held?: boolean } & QueueSortProps) {
  const h = (label: string, k: QueueSortKey, cls: string) => <SortHeader label={label} sortKey={k} sort={sort} onSort={onSort} className={cls} />;
  return (
    <div className={foot ? 'qcard qcard-paged' : 'qcard'} ref={cardRef}>
      <table className="lp-table qtable" {...area} data-held={held ? 'true' : undefined}>
        <colgroup><col className="qc-since" /><col className="qc-case" /><col className="qc-rec" /><col className="qc-need" /><col className="qc-act" /><col className="qc-sf" /><col className="qc-key" /><col className="qc-more" /></colgroup>
        <thead>
          <tr>
            {h(since, 'since', 'qh-since')}{h('Case', 'case', 'qh-case')}{h('Record', 'record', 'qh-rec')}{h('What to do', 'need', 'qh-need')}
            {h('Action', 'action', 'qh-act')}{h(SF_COLUMN_WORDS, 'links', 'qh-sf')}<PlainHeader label={KEY_WORDS.column} /><PlainHeader label="More" />
          </tr>
        </thead>
        {children}
      </table>
      {foot}
    </div>
  );
}

/** THE LIST BEFORE ITS FIRST ANSWER, IN ITS OWN COLUMNS (ported 2026-10-18 from the medrec console's post-ship review, its PR #183, finding
 *  11: two bars across the whole list made every column appear at once when the rows landed, instead of each filling its own shape). The
 *  table's own header (its sort presses live), then skeleton rows at the rows' 48 px: each cell in its column's shape, a bar at the column's
 *  usual width, a shorter second line where the row has two, the action's 28 px block, Salesforce's two squares, the key's circle and the
 *  More square; the shared `sk-pulse`. The rows are hidden from a screen reader, which hears QueryState's loading words once. */
const SKELETON_CELLS: readonly [cls: string, shape: ReactNode][] = [
  ['lp-since', <span className="qs-sk sk-since" />],
  ['qcase', <><span className="qs-sk sk-client" /><span className="qs-sk sk-l2 sk-client2" /></>],
  ['qins', <><span className="qs-sk sk-carrier" /><span className="qs-sk sk-l2 sk-policy" /></>],
  ['qneed', <><span className="qs-sk sk-what" /><span className="qs-sk sk-l2 sk-what2" /></>],
  ['qact', <span className="qs-sk sk-act" />],
  ['qsf', <span className="sk-sqs"><span className="qs-sk sk-sq" /><span className="qs-sk sk-sq" /></span>],
  ['qkey', <span className="qs-sk sk-key" />],
  ['qmorecell', <span className="qs-sk sk-sq" />],
];
export function QueueSkeleton({ since, sort, onSort }: { since?: string } & QueueSortProps) {
  return (
    <QueueTable sort={sort} onSort={onSort} since={since}>
      <tbody aria-hidden="true">
        {Array.from({ length: LIST_SKELETON_ROWS }, (_, i) => (
          <tr key={i} className="qsk-row" data-testid="list-skeleton-row">
            {SKELETON_CELLS.map(([cls, shape]) => <td key={cls} className={`sk-c ${cls}`}>{shape}</td>)}
          </tr>
        ))}
      </tbody>
    </QueueTable>
  );
}

/** The case cell: the client (opens the Matter in Salesforce, as before), at most two chips, then DOL, state and case type. The client's
 *  hover starts with the whole name, so a name cut with an ellipsis is still readable (the medrec console's PR #177, A9). */
function CaseCell({ it, sub, title }: { it: TodoItem; sub: ReactNode; title: string }) {
  const m = it.matter, r = it.row;
  return (
    <td className="qcase">
      <span className="lp-l1 ql1"><a className="sflink" href={sfMatterUrl(m.id)} target="_blank" rel="noreferrer" title={title}><b className="lp-name">{m.client}</b></a>
        {it.urgent ? <UrgentChip t={it.urgent} /> : null}{!m.lead && m.companionId ? <span className="pchip" title="Second client from the same crash">2nd</span> : null}{r.doNotUse ? <DoNotUseChip name={r.doNotUse} /> : null}</span>
      <span className="lp-l2 qsub" title={caseTitle(m)}>{sub}</span>
    </td>
  );
}
/** The record cell: the side's tag and the carrier, the policy number under it (an id, in Fira Code); or what is missing. */
function RecordCell({ it }: { it: TodoItem }) {
  const r = it.row;
  const realCarrier = r.carrier && !notACarrierName(r.carrier) ? r.carrier : null;
  return (
    <td className="qins" title={realCarrier ? `${realCarrier}${r.policy ? ` · policy ${r.policy}` : ' · no policy #'}` : r.carrier ? `The record says "${r.carrier}", which is not an insurer` : undefined}>
      <span className="lp-l1"><SidePill side={r.side} />{' '}{r.gap ? <i className="mt">no {r.side} record</i> : realCarrier ? <b className="qcar">{realCarrier}</b> : <i className="mt">no carrier yet</i>}</span>
      {realCarrier && !r.gap ? <span className="lp-l2"><small className="mono">{r.policy ?? 'no policy #'}</small></span> : null}
    </td>
  );
}
/** The row's own panel button (›), its key and its links: the last three cells of every row. */
function RowEnd({ it, entries, open, setOpen, hint }: { it: TodoItem; entries: KeyEntry[]; open: boolean; setOpen: (o: boolean) => void; hint: string }) {
  return (
    <>
      <td className="qsf"><SfLinks hrefs={sfHrefs(it.matter.id, it.row.insuranceId)} /></td>
      <td className="qkey"><RowKey name={KEY_WORDS.buttonName(it.matter.client)} entries={entries} /></td>
      <td className="qmorecell">
        <button type="button" className="iconbtn sm qkebab" aria-expanded={open} aria-label="More" title={hint}
          onClick={() => setOpen(!open)}><Ic name={open ? 'chevd' : 'chevl'} cls={open ? 'sm' : 'sm flip'} /></button>
      </td>
    </>
  );
}
/** The Approve strip under a row (facts.tsx ApproveClaim): its own full-width line, never squeezed into the action cell. */
function ApproveRow({ children }: { children: ReactNode }) {
  return <tr className="qsubrow"><td colSpan={COLS} className="q-wide">{children}</td></tr>;
}
/** The row's panel, full width under it. */
function PanelRow({ children }: { children: ReactNode }) {
  return <tr className="qpanelrow"><td colSpan={COLS} className="q-wide"><div className="qmore">{children}</div></td></tr>;
}

/** Codex finding 1 on the fix branch: the line under a row whose record's typed policy number failed (or needs a person). Codex round 2 (finding 2):
 *  hidden while the open panel lists that failed entry itself (FactLines shows the row's two newest facts), so it is said once */
export function foundStatusLine(it: Pick<TodoItem, 'foundStatus' | 'row'>, open: boolean): string | null {
  const s = it.foundStatus;
  if (!s) return null;
  if (open && s.kind === 'not_written' && (it.row.facts ?? []).slice(0, 2).some((f) => f.kind === 'policy_number' && f.state === 'FAILED')) return null;
  return `Policy number · ${foundStatusWords(s)}`;
}
/** One row of the Queue: one insurance record (or missing side) and the one thing a person does for it. At most two chips (Kimi's layout
 *  pass T3): the urgency chip and "2nd" for a second client; the injury, tier and limits chips move to the expansion (the ranking still uses
 *  them). A carrier that is not an insurer reads "no carrier yet" (T4). */
export function QueueRow({ it, now, place = '' }: { it: TodoItem; now: Date; /** its group or fold, for the held list (list/hold.ts) */ place?: string }) {
  const [open, setOpen] = useState(false);
  const r = it.row, m = it.matter;
  // held under the pointer while the newest answer no longer has this row as it is: inert and dimmed (list/hold.ts)
  const gone = useContext(HeldGoneContext)(queueRowSig(place, it));
  const me = useMe().data;
  const kinds = factKindsFor(r, { lorSend: me?.lorSend === true, lorOnFile: me?.lorOnFile === true, policeOrder: me?.policeOrder === true });
  const inline = kinds.length > 0 && INLINE_KINDS.has(kinds[0]!) && it.group !== 'reply';
  const reply = it.group === 'reply' ? replyOf(it) : null;
  // the wait: red once it is overdue (core queue.ts ageTone: over 3 days), the plain ink before; never amber (the design's amber is someone
  // else being late)
  const hot = ageTone(r.waitingSince, now) === 'old';
  // THE SYSTEM IS DOING THE WORK (the medrec console's system-working look, ported 2026-10-23; system-working.tsx): a row of "The system is
  // handling N" wears the calm work tint, the "System working" tag before its sentence and a bar at the top of its panel; what it offers is
  // unchanged
  const working = systemHandles(r);
  const foundLine = foundStatusLine(it, open);
  return (
    <tbody className={`qrowwrap${open ? ' open' : ''}${gone ? ' q-gone' : ''}`} inert={gone || undefined} data-gone={gone ? 'true' : undefined}>
      {/* the row whose panel is open is marked selected, in the shared selection fill (the medrec console's PR #177, A2: the list says which
          row the open panel is about); the selection's blue wins over the work tint */}
      <tr className={working ? 'qrow lp-r sw-row' : 'qrow lp-r'} {...(open ? { 'aria-selected': true } : {})}>
        <SinceCell label={waited(r.waitingSince, now.getTime())} tone={hot ? 'hot' : null} title={`Waiting since ${pacificStamp(r.waitingSince)}${hot ? ' (over 3 days)' : ''}`} />
        <CaseCell it={it} title={`${m.client} · Open in Salesforce · ${caseTitle(m)}`} sub={<>{clientLine(m)}</>} />
        <RecordCell it={it} />
        <td className="qneed" title={needHover(it, now)}><span className="lp-l1 qneed-t qneed-2">{working ? <SystemWorkingTag r={r} /> : null}{needText(it, now)}</span></td>
        <ActionCell className="lp-act qact"><QueueAction it={it} onOpen={() => setOpen(true)} /></ActionCell>
        <RowEnd it={it} entries={queueKeyEntries(it, now)} open={open} setOpen={setOpen} hint="More: why, the last call, every fix, what happened, Chatter, flag for Rod" />
      </tr>
      {it.group !== 'reply' ? <ApproveRow><ApproveClaim r={r} matterId={m.id} /></ApproveRow> : null}
      {/* Codex finding 1 on the fix branch: the record's typed policy number failed (or needs a person) and this row is its one place in the Queue */}
      {foundLine ? <ApproveRow><span className="mt" data-testid="qfound-status">{foundLine}</span></ApproveRow> : null}
      {/* a letter row's links on the row itself, before any send decision (Codex 10-21 r3 #3); the panel no longer repeats them */}
      {r.lorAsk && r.lorEvidence?.length ? <ApproveRow><LorEvidenceLine r={r} /></ApproveRow> : null}
      {/* the police report behind "names <carrier>": its links, and Approve when one insurer matched (2026-10-22) */}
      {r.policeEvidence ? <ApproveRow><PoliceApprove r={r} matterId={m.id} /></ApproveRow> : null}
      {open ? (
        <PanelRow>
          {/* while the system does the work, the bar first: "System doing work: <what it is doing>" (the medrec console's card bar); it says the
              row's own words, so the bold line that would say them again is left out */}
          {working ? <SystemWorkingBar r={r} /> : null}
          {/* the expansion's order (Kimi's layout pass T6): why first; the last call summary in full where reading it is the to-do; the reply
              thread; then what happens after a typed fact, only the part that is certain */}
          {it.why ? <div className="cm-text"><b>Why:</b> {it.why}</div> : working ? null : <div className="cm-text"><b>{queueWords(r.text)}</b></div>}
          {r.fix === 'review' && r.lastCall?.note ? <div className="cm-text qsum"><b>The last call's summary ({pacificStamp(r.lastCall.at)}):</b> “{queueWords(r.lastCall.note)}”</div>
            : r.lastCall ? <div className="cm-text">{queueWords(evidence(r, true))}{r.lastCall.note ? <> · <i>“{queueWords(r.lastCall.note)}”</i></> : null}</div> : null}
          {r.nextAt ? <div className="cm-text">Next try {pacificStamp(r.nextAt)}</div> : null}
          {reply || r.reply ? <div className="cm-text"><ReplyLine r={(reply ?? r.reply)!} all={r.replies ?? [(reply ?? r.reply)!]} /></div> : null}
          {r.facts?.length ? <div className="cm-text"><FactLines facts={r.facts} /></div> : null}
          {m.priority?.chips.length ? <div className="cm-text"><PriorityChips p={m.priority} /></div> : null}
          {inline ? <div className="cm-text mt">{AFTER_SAVE}</div> : null}
          <div className="ifix">
            {me?.writeBack === true && kinds.length ? <FactAct kinds={kinds} r={r} matterId={m.id} /> : r.status === 'need' || r.lorAsk ? keyed(allFixes(r, m.id)) : null}
            <Link className="btn" to={`/matters/${m.id}`} {...toneProps(actionToneOf('console', 'what_happened'))}><Ic name="list" cls="sm" />What happened</Link>
            <ChatterTeam matterId={m.id} />
            <a className="btn" href={sfMatterUrl(m.id)} target="_blank" rel="noreferrer" {...toneProps(actionToneOf('console', 'salesforce'))}><Ic name="ext" cls="sm" />Salesforce</a>
            <FlagForRod r={r} matterId={m.id} />
          </div>
        </PanelRow>
      ) : null}
    </tbody>
  );
}

/** A group's heading row, sticky under the column header (Kimi's layout pass T2): the task (verb first), how many, then one grey line: the
 *  longest wait and how to do it (the medrec console's group heading anatomy). */
export function QueueGroupHead({ g, now = new Date() }: { g: TodoGroupView; now?: Date }) {
  const oldest = oldestWait(g);
  const how = TODO_GROUPS[g.key].how;
  return (
    <tbody className="qgroup-head">
      <GroupRow columns={COLS} title={g.label} count={g.items.length} testid={`qgroup-${g.key}`}
        line={oldest ? `oldest ${waited(oldest, now.getTime())} \u{00B7} ${how}` : how} lineTitle={oldest ? `The longest wait in this group: since ${pacificStamp(oldest)}. ${how}` : how} />
    </tbody>
  );
}

/** "Show more (N)", under a group's first rows. */
function MoreRow({ n, onPress }: { n: number; onPress: () => void }) {
  return <tbody className="qmore-tb"><tr><td colSpan={COLS} className="q-wide"><button type="button" className="tmore" onClick={onPress}>Show more ({n})</button></td></tr></tbody>;
}

/** POLICY-LIMITS SEARCHES THE CONTROLLER SUGGESTS (Coastal Research; Rod 2026-10-21 "incorporate both of those api tools into claims
 *  console"; the three-views rule: a new automation gets a small Queue group only where a person must act). A regular group in the Queue's
 *  table: the same heading, columns, 48 px rows and 5-row cap; its place in the group order is after "Find the at-fault driver's insurer".
 *  Rows: suggestions nobody has answered (core coastal.ts canDecide), oldest first; Approve / Not now in the Action column; the why in words,
 *  never a dollar figure (decision B). The suggested cases are older than the Queue's windows, so the group is the same on Today and 14 days.
 *  Hidden unless the console's COASTAL is on. */
export const LIMITS_GROUP = Object.freeze({ title: 'Approve a policy-limits search', order: 6.5,
  how: "Coastal Research finds the at-fault driver's real limits: Approve orders one search, Not now asks again in 14 days" });
function LimitsRow({ r, now }: { r: CoastalRow; now: Date }) {
  const hot = ageTone(r.since, now) === 'old';
  return (
    <tbody className="qrowwrap" data-testid="qlimits-row">
      <tr className="qrow lp-r">
        <SinceCell label={waited(r.since, now.getTime())} tone={hot ? 'hot' : null} title={`Suggested ${pacificStamp(r.since)}${hot ? ' (over 3 days)' : ''}`} />
        <td className="qcase">
          <span className="lp-l1 ql1"><a className="sflink" href={sfMatterUrl(r.matterId)} target="_blank" rel="noreferrer" title={`${r.client ?? 'The case'} · Open in Salesforce`}>
            <b className="lp-name">{r.client ?? 'Case not found in Salesforce'}</b></a>
            {r.env === 'test' ? <span className="pchip" title="Coastal Research test account: nothing is ordered or billed">test</span> : null}</span>
          <span className="lp-l2 qsub">{clientLine({ state: r.matterState, dol: r.dol }, r.stage)}</span>
        </td>
        <td className="qins" title={r.carrier ? `${r.carrier} · the at-fault driver's policy` : undefined}>
          <span className="lp-l1"><SidePill side="DEF" />{' '}{r.carrier ? <b className="qcar">{r.carrier}</b> : <i className="mt">no carrier yet</i>}</span>
        </td>
        <td className="qneed" title={`${r.why}\n${r.status}${r.advocate ? `\nClient advocate: ${r.advocate}` : ''}`}><span className="lp-l1 qneed-t qneed-2">{r.why}</span></td>
        <ActionCell className="lp-act qact"><Decide orderId={r.orderId} matterId={r.matterId} compact /></ActionCell>
        <td className="qsf"><SfLinks hrefs={sfHrefs(r.matterId, r.insuranceId)} /></td>
        <td className="qkey" />
        <td className="qmorecell"><Link className="iconbtn sm qkebab" to={`/matters/${r.matterId}`} aria-label="Open the case" title="Open the case: what happened, its police report and policy limits">
          <Ic name="chevl" cls="sm flip" /></Link></td>
      </tr>
    </tbody>
  );
}
export function LimitsGroup({ rows, now }: { rows: CoastalRow[]; now: Date }) {
  const [open, setOpen] = useState(false);
  if (!rows.length) return null;
  const shown = open || rows.length <= QUEUE_CAP ? rows : rows.slice(0, QUEUE_CAP);
  const oldest = rows.reduce<string | null>((best, r) => (!best || Date.parse(r.since) < Date.parse(best) ? r.since : best), null);
  return (
    <>
      <tbody className="qgroup-head">
        <GroupRow columns={COLS} title={LIMITS_GROUP.title} count={rows.length} testid="qgroup-limits"
          line={oldest ? `oldest ${waited(oldest, now.getTime())} \u{00B7} ${LIMITS_GROUP.how}` : LIMITS_GROUP.how}
          lineTitle={oldest ? `The longest wait in this group: since ${pacificStamp(oldest)}. ${LIMITS_GROUP.how}` : LIMITS_GROUP.how} />
      </tbody>
      {shown.map((r) => <LimitsRow key={r.orderId} r={r} now={now} />)}
      {shown.length < rows.length ? <MoreRow n={rows.length - shown.length} onPress={() => setOpen(true)} /> : null}
    </>
  );
}

/** FOUND A NUMBER (2026-10-28, spec docs/superpowers/specs/2026-10-28-found-number-approve-design.md; the three-slot rule): the last 30 days' dialed
 *  "policy not found" misses whose files now hold a different policy number, and (widened 10-29) the active claimless records with no request in 30
 *  days whose files hold a number the record lacks or contradicts; only the rows a person must act on (core foundQueueRows). A regular group in the Queue's table: the same heading, columns, 48 px
 *  rows and 5-row cap, placed just before "Enter the policy number"; Approve, or why there is none, or what happened after it, on each row's strip.
 *  The same on Today and 14 days (its window is its own 30 days). */
export const FOUND_GROUP = Object.freeze({ title: 'Found a number', order: 3.9,
  how: 'Our files hold a policy number the record lacks or contradicts: Approve writes it, and the AI calls if and when the record can be called (once more 2 weekdays later if still not found)',
  /** the Approve button off (a switch, or the hard lock until the router stands down: Codex r1 F9): only rows a person acts on by opening the case */
  howOff: 'A policy number for these records needs a person: open the case' });
function FoundQueueRow({ row, now, approveOn }: { row: FoundRow; now: Date; approveOn: boolean }) {
  const hot = ageTone(row.at, now) === 'old';
  const foundNeed = row.status ? foundStatusWords(row.status) : row.offers.length > 1 ? `Our files hold ${row.offers.length} numbers: pick one`
    : row.offer ? `Our files hold ${row.offer}` : foundBlockWords(row);
  // a files-lane row is as old as its newest dated document; an intake-only number has no date, so the row never claims one
  const since = row.lane === 'miss' ? `The call missed ${pacificStamp(row.at)}`
    : row.sources.some((x) => x.at) ? `Our files have held it since ${pacificStamp(row.at)}` : 'Our files hold it (the intake carries no date)';
  return (
    <tbody className="qrowwrap" data-testid="qfound-row">
      <tr className="qrow lp-r">
        <SinceCell label={waited(row.at, now.getTime())} tone={hot ? 'hot' : null} title={`${since}${hot ? ' (over 3 days)' : ''}`} />
        <td className="qcase">
          <span className="lp-l1 ql1"><a className="sflink" href={sfMatterUrl(row.matterId)} target="_blank" rel="noreferrer" title={`${row.client ?? 'The case'} · Open in Salesforce`}>
            <b className="lp-name">{row.client ?? row.matterName ?? 'Case'}</b></a></span>
          <span className="lp-l2 qsub" title={row.lane === 'miss' ? `${row.canName} · ${row.reason === 'notFound' ? "the carrier couldn't find the policy" : 'the carrier asked for the policy number'}${row.missReason ? `\n${row.missReason}` : ''}` : 'No call yet: a number in our files'}>{clientLine(row)}</span>
        </td>
        <td className="qins" title={row.carrier ?? undefined}>
          <span className="lp-l1"><SidePill side={row.side} />{' '}{row.carrier ? <b className="qcar">{row.carrier}</b> : <i className="mt">no carrier yet</i>}</span>
          <span className="lp-l2"><small className="mono">{row.lane === 'miss' ? `AI told ${row.told ?? 'no policy #'}` : `record has ${row.current ?? 'no policy #'}`}</small></span>
        </td>
        <td className="qneed" title={foundNeed ?? undefined}><span className="lp-l1 qneed-t qneed-2">{foundNeed}</span></td>
        <ActionCell className="lp-act qact" />
        <td className="qsf"><SfLinks hrefs={sfHrefs(row.matterId, row.insuranceId)} /></td>
        <td className="qkey" />
        <td className="qmorecell"><Link className="iconbtn sm qkebab" to={`/matters/${row.matterId}`} aria-label="Open the case" title="Open the case: what happened">
          <Ic name="chevl" cls="sm flip" /></Link></td>
      </tr>
      <ApproveRow><FoundApprove row={row} approveOn={approveOn} /></ApproveRow>
    </tbody>
  );
}
export function FoundGroup({ rows, now, approveOn, notes }: { rows: FoundRow[]; now: Date; approveOn: boolean; notes: string[] }) {
  const [open, setOpen] = useState(false);
  if (!rows.length && !notes.length) return null;
  const shown = open || rows.length <= QUEUE_CAP ? rows : rows.slice(0, QUEUE_CAP);
  const oldest = rows.reduce<string | null>((best, r) => (!best || Date.parse(r.at) < Date.parse(best) ? r.at : best), null);
  const how = approveOn ? FOUND_GROUP.how : FOUND_GROUP.howOff;
  return (
    <>
      <tbody className="qgroup-head">
        <GroupRow columns={COLS} title={FOUND_GROUP.title} count={rows.length} testid="qgroup-found"
          line={oldest ? `oldest ${waited(oldest, now.getTime())} \u{00B7} ${how}` : how}
          lineTitle={oldest ? `The longest wait in this group: since ${pacificStamp(oldest)}. ${how}` : how} />
      </tbody>
      {notes.length ? <tbody className="qmore-tb"><tr><td colSpan={COLS} className="q-wide mt">Can't check every miss right now ({notes.join('; ')})</td></tr></tbody> : null}
      {shown.map((r) => <FoundQueueRow key={r.insuranceId} row={r} now={now} approveOn={approveOn} />)}
      {shown.length < rows.length ? <MoreRow n={rows.length - shown.length} onPress={() => setOpen(true)} /> : null}
    </>
  );
}

/** The sort's options for this viewer: what each row's Action column shows depends on what the console lets them do. */
function useSortOpts(): { lorSend: boolean; lorOnFile: boolean; policeOrder: boolean; writeBack: boolean } {
  const me = useMe().data;
  return { lorSend: me?.lorSend === true, lorOnFile: me?.lorOnFile === true, policeOrder: me?.policeOrder === true, writeBack: me?.writeBack === true };
}

/** One group: its heading, its first rows (QUEUE_CAP) in the sort, then "Show more (N)". */
export function QueueGroup({ g, now, sort = QUEUE_SORT.default }: { g: TodoGroupView; now: Date; sort?: ListSort<QueueSortKey> }) {
  const [open, setOpen] = useState(false);
  const opts = useSortOpts();
  const { shown, more } = capGroup({ ...g, items: sortQueueItems(g.items, sort, opts) }, open);
  return (
    <>
      <QueueGroupHead g={g} now={now} />
      {shown.map((it) => <QueueRow key={itemKey(it)} it={it} now={now} place={g.key} />)}
      {more ? <MoreRow n={more} onPress={() => setOpen(true)} /> : null}
    </>
  );
}

/** A folded line: "The system is handling 41" opens to the rows (in the sort). */
export function QueueFold({ label, sub, items, now, sort = QUEUE_SORT.default, place = '' }: { label: string; sub: string; items: TodoItem[]; now: Date; sort?: ListSort<QueueSortKey>;
  /** which fold, for the held list (list/hold.ts) */ place?: string }) {
  const [open, setOpen] = useState(false);
  const opts = useSortOpts();
  if (!items.length) return null;
  return (
    <>
      <tbody className="qfoldwrap">
        <tr><td colSpan={COLS} className="q-wide">
          <button type="button" className="tfold" aria-expanded={open} onClick={() => setOpen(!open)}>
            <Ic name={open ? 'chevd' : 'chevl'} cls="sm" /><b>{label}</b><span className="mt"> · {sub}</span>
          </button>
        </td></tr>
      </tbody>
      {open ? sortQueueItems(items, sort, opts).map((it) => <QueueRow key={itemKey(it)} it={it} now={now} place={place} />) : null}
    </>
  );
}

/** "a call scheduled 12 · waiting on a police report 5 · ..." from the working rows' own next move */
export function workingSub(items: TodoItem[]): string {
  const parts = workingSplit(items);
  return parts.length ? `${parts.map((p) => `${countOf(p.count)} ${p.label}`).join(' · ')}; nothing for you` : 'nothing for you';
}

/** The Queue: one group per thing a person does (client replies first), then the folds: new rows in their first 15 minutes, the rows the
 *  system is handling, and the rows a rule should close on its own. */
export function QueueList({ q: given, empty, now = new Date(), search = '', onClearSearch, sort = NO_SORT.sort, onSort = NO_SORT.onSort, view = '', limits = [], found = [], approveOn = false, foundNotes = [] }: { q: TodoBoard; empty: string; now?: Date;
  search?: string; onClearSearch?: () => void;
  /** the policy-limits suggestions waiting for a person (COASTAL on; LIMITS_GROUP), drawn in their place in the group order */ limits?: CoastalRow[];
  /** the Found-a-number rows (FOUND_GROUP), drawn in their place */ found?: FoundRow[]; approveOn?: boolean; foundNotes?: string[];
  /** the list a person asked for (the page's address and its search): a newer answer for it waits while the pointer is in the table */ view?: string } & Partial<QueueSortProps>) {
  // ROWS NEVER MOVE UNDER THE POINTER (list/hold.ts): a saved row leaves its group on the next read; until the pointer leaves the table the
  // rows stay where they are
  const { shown: q, held, area } = useHoldWhilePointed(given, view, false, queueSigLine);
  const live = useMemo(() => (held ? queueSigs(given) : null), [held, given]);
  const gone = useCallback((sig: string) => live !== null && !live.has(sig), [live]);
  return (
    <div className="qlist">
      <HeldGoneContext.Provider value={gone}>
      <QueueTable sort={sort} onSort={onSort} area={area} held={held}>
        {q.groups.length || limits.length || found.length || foundNotes.length ? withExtras(q.groups.map((g) => ({ order: TODO_GROUPS[g.key].order, node: <QueueGroup key={g.key} g={g} now={now} sort={sort} /> })), [
          ...(limits.length ? [{ order: LIMITS_GROUP.order, node: <LimitsGroup key="limits" rows={limits} now={now} /> }] : []),
          ...(found.length || foundNotes.length ? [{ order: FOUND_GROUP.order, node: <FoundGroup key="found" rows={found} now={now} approveOn={approveOn} notes={foundNotes} /> }] : []),
        ]) : (
          // empty, or no match: one line that closes the loop, and the next step where there is one (the medrec console's states)
          <tbody className="qempty"><tr><td colSpan={COLS} className="q-wide">
            {search && onClearSearch ? <EmptyState inline words={STATE_WORDS.noMatch(search)} next={{ label: STATE_WORDS.clearSearch, onPress: onClearSearch }} />
              : <EmptyState inline words={empty} />}
          </td></tr></tbody>
        )}
        <QueueFold label={`${countOf(q.fresh.length)} new in the last 15 minutes`} sub="the system gets the first try; they join the list if it can't solve them" items={q.fresh} now={now} sort={sort} place="fresh" />
        <QueueFold label={`The system is handling ${countOf(q.working.length)}`} sub={workingSub(q.working)} items={q.working} now={now} sort={sort} place="working" />
        <QueueFold label={`${countOf(q.parked.length)} the system should close on its own`} sub="the client already said they have no auto insurance; not a to-do" items={q.parked} now={now} sort={sort} place="parked" />
      </QueueTable>
      </HeldGoneContext.Provider>
    </div>
  );
}

/** the groups in their order, each extra group (policy limits, found a number) in its place by its order */
function withExtras(groups: { order: number; node: ReactNode }[], extras: { order: number; node: ReactNode }[]): ReactNode[] {
  return [...groups, ...extras].map((g, i) => ({ ...g, i })).sort((a, b) => a.order - b.order || a.i - b.i).map((g) => g.node);
}

/** The one number the Queue keeps from the scoreboard (the design: "the Queue keeps one line: today's claim numbers against the goal"), as
 *  small text in the bar; the whole scoreboard is one click away in All cases. Same rule as the Matters tile and the 5:30 pm email. */
export function QueueGoal({ t, onNumbers }: { t: OpenedToday | null | undefined; onNumbers: () => void }) {
  if (t === undefined) return null;
  return (
    <button type="button" className="linkbtn qgoal" onClick={onNumbers}
      title="Claim numbers first entered today on a PLT or DEF record (the 5:30 pm Claims Team report's rule; second clients are not counted). Opens All cases with the whole scoreboard.">
      {t ? <><b>{countOf(t.total)}</b> of {t.goal} today</> : 'claim numbers today: not available'} ›</button>
  );
}

/** The Queue's bar, sticky under the header (~48 px): the tabs with their counts, then the filters, then the goal and the links. */
export function QueueBar({ tabs, filters, right }: { tabs: ReactNode; filters: ReactNode; right: ReactNode }) {
  // the group headers stick right under the bar: its real height (it wraps to two lines on a narrow window) goes to --qbar
  const ref = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const set = () => document.documentElement.style.setProperty('--qbar', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return <div className="qbar" ref={ref} aria-live="polite">{tabs}{filters}<span className="grow" />{right}</div>;
}

// ---- All unopened (phase 1b, Rod 2026-10-13 9:15 pm "we need to be able to see all time unopened claims") ----------------------------------
// The same row anatomy over a LIGHT record (cheap reads only, core unopened.ts): the age pill is the case's age in days, months or years
// (ageWords; the exact days in the hover) with the Queue's color rule, the Need is the light reason with its facts under it, the action is the input
// for the fact that unblocks it where the controller writes (Open cases in its active stages), else "Open in Salesforce". The kebab reads the
// case's full answer (the case page's) and shows this record's real Need and every fix.

/** the record's full row, read when its panel opens (the case page's answer; cached per case like that page), under the panel's own links.
 *  While it reads, its shape (the medrec console's post-ship review, its PR #183, finding 6: the panel drawn before its answer holds the
 *  answer's layout): the Need line, the last call's line and the fixes' block as skeleton bars; the box never shrinks below that once the
 *  answer lands, so a short answer (the case unreadable) does not pull the rows under the panel up. */
function FullNeed({ rec }: { rec: UnopenedRec }) {
  const d = useQuery({ queryKey: ['matter', rec.matterId], queryFn: () => api.matter(rec.matterId), staleTime: 60_000 });
  const box = useRef<HTMLDivElement>(null);
  const [floor, setFloor] = useState<number | null>(null);
  useLayoutEffect(() => { if (d.isLoading && box.current) setFloor(box.current.offsetHeight); }, [d.isLoading]);
  return <div className="fullneed" ref={box} style={!d.isLoading && floor !== null ? { minHeight: floor } : undefined} data-testid="full-need">{fullNeedBody(d, rec)}</div>;
}
function fullNeedBody(d: { isLoading: boolean; error: unknown; data: Awaited<ReturnType<typeof api.matter>> | undefined }, rec: UnopenedRec): ReactNode {
  if (d.isLoading) {
    return (
      <div className="fullneed-sk" aria-busy="true">
        <span className="qs-sr">Reading this case's full picture (a few seconds)…</span>
        <div className="cm-text" aria-hidden="true"><span className="qs-sk sk-fn1" /></div>
        <div className="cm-text" aria-hidden="true"><span className="qs-sk sk-fn2" /></div>
        <div className="ifix" aria-hidden="true"><span className="qs-sk sk-act" /></div>
      </div>
    );
  }
  if (d.error || !d.data) return <div className="cm-text mt">The case's full picture is not available right now; the light reason above still holds.</div>;
  const row = d.data.matter.rows.find((r) => r.insuranceId.slice(0, 15) === rec.insuranceId.slice(0, 15));
  if (!row) return <div className="cm-text mt">This record is not on the case's board (it may have a claim number by now).</div>;
  return (
    <>
      <div className="cm-text"><b>{queueWords(row.text)}</b>{row.need ? <> · <b>Need:</b> {queueWords(row.need)}</> : null}</div>
      {row.lastCall ? <div className="cm-text">{queueWords(evidence(row, true))}{row.lastCall.note ? <> · <i>“{queueWords(row.lastCall.note)}”</i></> : null}</div> : null}
      {row.status === 'need' || row.lorAsk ? <div className="ifix">{keyed(allFixes(row, rec.matterId))}</div> : null}
    </>
  );
}

/** One record of All unopened. */
export function UnopenedRow({ rec, now }: { rec: UnopenedRec; now: Date }) {
  const [open, setOpen] = useState(false);
  // held under the pointer while the newest page no longer has this record as it is: inert and dimmed (list/hold.ts)
  const gone = useContext(HeldGoneContext)(JSON.stringify(rec));
  const it = unopenedItem(rec);
  const r = it.row, m = it.matter;
  const days = caseAgeDays(rec.openedAt, now);
  // the press in place where the controller writes (core unopenedAction: the server sorts the Action column by the same answer)
  const inline = unopenedAction(rec);
  return (
    <tbody className={`qrowwrap${open ? ' open' : ''}${gone ? ' q-gone' : ''}`} inert={gone || undefined} data-gone={gone ? 'true' : undefined}>
      {/* the open row marked selected, as the Queue's (A2) */}
      <tr className="qrow lp-r" {...(open ? { 'aria-selected': true } : {})}>
        {/* the case's age, never red: every row here is unopened, so red would be every row (the row's key says what it counts) */}
        <SinceCell label={ageWords(days)} tone={null} title={`Case open ${days.toLocaleString('en-US')} day${days === 1 ? '' : 's'} (since ${pacificStamp(rec.openedAt)})`} />
        <td className="qcase">
          <span className="lp-l1 ql1"><a className="sflink" href={sfMatterUrl(m.id)} target="_blank" rel="noreferrer" title={`${m.client} · Open in Salesforce · ${m.name} · ${rec.status}${rec.stage ? ` · ${rec.stage}` : ''}`}><b className="lp-name">{m.client}</b></a>
            {!m.lead && m.companionId ? <span className="pchip" title="Second client from the same crash">2nd</span> : null}{rec.doNotUse ? <DoNotUseChip name={rec.doNotUse} /> : null}</span>
          <span className="lp-l2 qsub">{clientLine(m, rec.status === 'Litigation' ? 'in litigation' : null)}</span>
        </td>
        <RecordCell it={it} />
        <td className="qneed" title={`${queueWords(r.need)}\n${TODO_GROUPS[it.group].label}: ${TODO_GROUPS[it.group].how}`}>
          <span className="lp-l1 qneed-t">{queueWords(r.need)}</span><span className="lp-l2 qfacts">{lightFacts(rec).join(' · ')}</span>
        </td>
        <ActionCell className="lp-act qact">
          {inline ? <InlineFact kind={inline} r={r} matterId={m.id} />
            : <a className="btn sm" href={sfMatterUrl(m.id)} target="_blank" rel="noreferrer" {...toneProps(actionToneOf('console', 'salesforce'))}
              title={rec.writable ? 'Fix it on the record in Salesforce' : `Typed facts are written only on Open cases in the claims system's active stages; this case is ${rec.status}${rec.stage ? `, ${rec.stage}` : ''}`}>
              <Ic name="ext" cls="sm" />Open in Salesforce</a>}
        </ActionCell>
        <RowEnd it={{ ...it, row: { ...r, doNotUse: rec.doNotUse ?? r.doNotUse } }} entries={unopenedKeyEntries({ ...it, row: { ...r, doNotUse: rec.doNotUse ?? r.doNotUse } }, { days, openedAt: rec.openedAt })}
          open={open} setOpen={setOpen} hint="More: this record's full Need (read now), every fix, what happened, Chatter, flag for Rod" />
      </tr>
      <ApproveRow><ApproveClaim r={r} matterId={m.id} writable={rec.writable} /></ApproveRow>
      {open ? (
        <PanelRow>
          {/* the panel's own links first, where the case's read never moves them (the medrec console's panel: its links at the top, the read's
              body under them; its PR #183, finding 6): the read lands below, so a pointer that went for one of them while it read stays on it */}
          <div className="ilinks" data-testid="panel-links">
            <Link className="btn" to={`/matters/${m.id}`} {...toneProps(actionToneOf('console', 'what_happened'))}><Ic name="list" cls="sm" />What happened</Link>
            <ChatterTeam matterId={m.id} />
            <a className="btn" href={sfMatterUrl(m.id)} target="_blank" rel="noreferrer" {...toneProps(actionToneOf('console', 'salesforce'))}><Ic name="ext" cls="sm" />Salesforce</a>
            <FlagForRod r={r} matterId={m.id} />
          </div>
          <FullNeed rec={rec} />
        </PanelRow>
      ) : null}
    </tbody>
  );
}

/** The All unopened filters, server-side, each with its count (every other filter applied). The state and the carrier are menus (dozens of
 *  values); the rest are buttons. */
export function UnopenedFilters({ page, params, set }: { page: UnopenedPage | undefined; params: Record<string, string>; set: (k: string, v: string | null) => void }) {
  const f = page?.facets;
  const n = (x: number | undefined) => (x === undefined ? '…' : x.toLocaleString('en-US'));
  const seg = (label: string, key: string, opts: [string, string, number | undefined][]) => (
    <div className="seg seg-state" role="group" aria-label={label}>
      <span className="segl" aria-hidden="true">{label}</span>
      <button type="button" aria-pressed={!params[key]} onClick={() => set(key, null)}>All</button>
      {opts.map(([v, l, c]) => <button key={v} type="button" aria-pressed={params[key] === v} onClick={() => set(key, params[key] === v ? null : v)}>{l}<span className="fc">{n(c)}</span></button>)}
    </div>
  );
  return (
    <>
      {seg('Case age', 'age', AGE_BUCKETS.map(([k, l]) => [k, l.replace('14 days or less', '≤ 14 d').replace('15 to 90 days', '15-90 d').replace('91 days to a year', '91 d-1 yr').replace('Over a year', '> 1 yr'), f?.age[k]]))}
      {seg('Side', 'side', [['PLT', 'PLT', f?.side.PLT], ['DEF', 'DEF', f?.side.DEF]])}
      <label className="qsel">State<select value={params.state ?? ''} onChange={(e) => set('state', e.target.value || null)}>
        <option value="">All</option>{(f?.state ?? []).map(([k, c]) => <option key={k} value={k}>{k === 'none' ? 'No state' : k} ({n(c)})</option>)}
      </select></label>
      <label className="qsel">Carrier<select value={params.carrier ?? ''} onChange={(e) => set('carrier', e.target.value || null)}>
        <option value="">All</option>
        <option value="none">No carrier ({n(f?.carrier.none)})</option>
        <option value="placeholder">Not an insurer ("Unknown", "No insurance") ({n(f?.carrier.placeholder)})</option>
        {(f?.carrier.top ?? []).map(([k, c]) => <option key={k} value={k}>{k} ({n(c)})</option>)}
      </select></label>
      {seg('Case', 'status', UNOPENED_STATUSES.map((st) => [st, st, f?.status.find(([k]) => k === st)?.[1] ?? (f ? 0 : undefined)]))}
      <div className="seg seg-state" role="group" aria-label="The system asks a person">
        <button type="button" aria-pressed={params.asked === '1'} onClick={() => set('asked', params.asked === '1' ? null : '1')}
          title="Only the records the claims system holds for a person (its open asks; the old Older tab)">The system asks a person<span className="fc">{n(f?.asked)}</span></button>
      </div>
      <label className="opt qchk" title="Second clients from the same crash carry the main client's claim; left out by default"><input type="checkbox" checked={params.second === '1'}
        onChange={(e) => set('second', e.target.checked ? '1' : null)} /> + second clients ({n(f?.second)})</label>
      <label className="opt qchk" title="Placeholders (DEF INS -, Pending Insurance Company) next to a claimed record of the same side, the same car or person: not open work; left out by default">
        <input type="checkbox" checked={params.leftovers === '1'} onChange={(e) => set('leftovers', e.target.checked ? '1' : null)} />
        {params.leftovers === '1' ? `leftover placeholders shown (${n(f?.leftovers)})` : `${n(f?.leftovers)} leftover placeholders hidden`}</label>
      <label className="opt qchk" title={`Test cases are left out: ${n(f?.tests.flag)} have Salesforce's "Not Include Test" box ticked, ${n(f?.tests.name)} have "test" or "tester" in the client's name`}>
        <input type="checkbox" checked={params.tests === '1'} onChange={(e) => set('tests', e.target.checked ? '1' : null)} />
        {params.tests === '1' ? `test cases shown (${n(f ? f.tests.flag + f.tests.name : undefined)})` : `${n(f ? f.tests.flag + f.tests.name : undefined)} test cases hidden`}</label>
    </>
  );
}

/** One page of All unopened, the rows in the server's order (oldest case first, or the column sort the address asks for, applied to the whole
 *  list before the page; never re-sorted here), under the medrec console's paged footer (PR #169): "Showing 101-193 of 193", "100 per
 *  page" and the shared pager (list/Pager.tsx), sticky at the window's bottom so the button pressed stays in view and keeps the focus. The
 *  page asked lives in the address (?page=, pages.tsx); a move brings the table's top back when it had scrolled out. */
export function UnopenedList({ page: given, now, onGo, sort, onSort, search = '', onClearSearch, view = '', placeholder = false }: { page: UnopenedPage; now: Date; onGo: (to: PageMove) => void;
  /** the top bar's words, searched on the server (core unopened.ts); here only for the no-match line */ search?: string; onClearSearch?: () => void;
  /** the page a person asked for (its filters, sort, search and page): a newer answer for it waits while the pointer is in the table */ view?: string;
  /** `page` is the last page's rows while this one reads (TanStack's placeholder): never held over */ placeholder?: boolean } & QueueSortProps) {
  // ROWS NEVER MOVE UNDER THE POINTER (list/hold.ts), as on the Queue's tabs
  const { shown: page, held, area } = useHoldWhilePointed(given, view, placeholder, unopenedSigLine);
  const live = useMemo(() => (held ? new Set(given.records.map((r) => JSON.stringify(r))) : null), [held, given]);
  const gone = useCallback((sig: string) => live !== null && !live.has(sig), [live]);
  const recs = page.records;
  // A MOVE OPENS THE NEW PAGE AT ITS FIRST ROW (the medrec pattern): the footer sits under 100 rows, so after a press the window would show
  // the new page's last rows; when the table's top has scrolled under the top bar and the Queue's bar, it comes back (scroll-margin-top in
  // claims.css is those two bars), and the footer, stuck to the window's bottom, keeps the button pressed in view and focused.
  const box = useRef<HTMLDivElement>(null);
  const go = (to: PageMove): void => {
    onGo(to);
    const el = box.current;
    if (el === null) return;
    const under = Number.parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
    if (el.getBoundingClientRect().top < under) el.scrollIntoView({ block: 'start' });
  };
  const foot = (
    <div className="qfoot" data-testid="un-foot">
      <span data-testid="un-showing">{showingWords(pageSpan({ page: page.page, pageSize: page.pageSize, total: page.total, shown: recs.length }))}</span>
      <span className="qfoot-per" data-testid="un-per-page">{PAGER_WORDS.perPage(page.pageSize)}</span>
      <Pager page={page.page} pages={page.pages} onGo={go} testid="un-pager" />
    </div>
  );
  return (
    <div className="qlist">
      <HeldGoneContext.Provider value={gone}>
      <QueueTable sort={sort} onSort={onSort} since="Age" foot={foot} cardRef={box} area={area} held={held}>
        <tbody className="qgroup-head">
          <GroupRow columns={COLS} title="All unopened claims" count={page.total} testid="qgroup-unopened"
            line="Every PLT / DEF record with no claim number on an open auto case; open a row for its full Need" />
        </tbody>
        {recs.length ? recs.map((rec) => <UnopenedRow key={rec.insuranceId} rec={rec} now={now} />)
          : <tbody className="qempty"><tr><td colSpan={COLS} className="q-wide">{search.trim() && onClearSearch
            ? <EmptyState inline words={STATE_WORDS.noMatch(search)} next={{ label: STATE_WORDS.clearSearch, onPress: onClearSearch }} />
            : <EmptyState inline words="Nothing unopened matches these filters." />}</td></tr></tbody>}
      </QueueTable>
      </HeldGoneContext.Provider>
    </div>
  );
}
